import { ColumnAccessor, ColumnAccessorSortable } from '@/types/columns';
import { DisplayMode } from '@/types/display-mode';
import { KPIComparison } from '@/types/kpi-comparison';
import { Store } from '@/types/store';

export interface BusinessColumnVisibilityParams {
  kpiComparison: KPIComparison;
  hasCouponsInCurrentView: boolean;
  hasAnyShopifyVariantId: boolean;
  hasDiscountedPrice: boolean;
  store: Store;
  isInternalUser: boolean;
  displayMode: DisplayMode;
  dealId: string;
}

/**
 * Offres pour lesquelles certaines colonnes (discount choose, prix choose final)
 * doivent être masquées aux utilisateurs Marque (non internes).
 */
export const BRAND_HIDDEN_COLUMNS_OFFER_ID: readonly string[] = [
  'd_019ef456aecb714cac68886609e4cb93',
];

/**
 * Indique si les colonnes/champs de pricing Choose (discount choose, prix choose
 * final) doivent être masqués : pour une Marque (non interne) sur une offre
 * listée dans BRAND_HIDDEN_COLUMNS_OFFER_ID.
 *
 * Source unique de vérité réutilisée par la visibilité des colonnes du tableau
 * (getBusinessColumnVisibility) et par la sidebar d'édition en masse.
 */
export function shouldHideBrandPricingColumns(
  isInternalUser: boolean,
  dealId: string
): boolean {
  return !isInternalUser && BRAND_HIDDEN_COLUMNS_OFFER_ID.includes(dealId);
}

/**
 * Returns visibility for business-driven columns (sortable / configurable).
 * Single source of truth for table columnVisibility and column-order modal.
 */
export function getBusinessColumnVisibility(
  params: BusinessColumnVisibilityParams
): Partial<Record<ColumnAccessor, boolean>> {
  const {
    kpiComparison,
    hasCouponsInCurrentView,
    hasAnyShopifyVariantId,
    hasDiscountedPrice,
    store,
    isInternalUser,
    displayMode,
    dealId,
  } = params;

  const none = kpiComparison === KPIComparison.NONE;
  const simplified = kpiComparison === KPIComparison.SIMPLIFIED;
  const discountModes =
    kpiComparison === KPIComparison.NONE ||
    kpiComparison === KPIComparison.SIMPLIFIED ||
    kpiComparison === KPIComparison.DISCOUNT_HIGHER ||
    kpiComparison === KPIComparison.DISCOUNT_LOWER;

  // Masquer discount choose et prix choose final aux Marques (non internes)
  // uniquement pour les offres ciblées.
  const hideBrandColumns = shouldHideBrandPricingColumns(
    isInternalUser,
    dealId
  );

  return {
    couponExpirationDate: hasCouponsInCurrentView,
    couponTerms: hasCouponsInCurrentView,
    couponHowToUse: hasCouponsInCurrentView,
    buyingPrice: none,
    chooseDiscount: discountModes && !hideBrandColumns,
    choosePrice: discountModes && !hideBrandColumns,
    description: none,
    discountedPrice:
      (none || simplified) && (isInternalUser || hasDiscountedPrice),
    gtin: none,
    listPrice: discountModes,
    madeIn: none,
    marginPercentage: isInternalUser ? none || simplified : false,
    potentialSalesVolume: none || simplified,
    shippingPrice: none || simplified,
    sku: none,
    shopifyVariantId: hasAnyShopifyVariantId,
    stockQuantity: none || kpiComparison === KPIComparison.STOCK,
    sourceUrl: none,
    productType: none,
    validationErrors: displayMode !== DisplayMode.DESELECTED,
    vatRate: store === Store.FR && (none || simplified),
    eeePrice: store === Store.FR && none,
  };
}

/**
 * Returns whether a sortable column is visible given current business params.
 * Use in column-order modal to filter which columns to show.
 * Columns not in the business visibility map (e.g. isBestSeller) are considered visible.
 */
export function isSortableColumnVisible(
  columnAccessor: ColumnAccessorSortable,
  params: BusinessColumnVisibilityParams
): boolean {
  const visibility = getBusinessColumnVisibility(params);
  const value = visibility[columnAccessor];
  return value === undefined ? true : value;
}
